
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getCategories, Category as ApiCategory } from '../api/products';
import { getImageUrl } from '../utils/image';
import { motion } from 'framer-motion';

interface CategoryItem {
    id: string;
    name: string;
    nameAr: string;
    image: string;
    slug: string;
}

const Categories: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const [categories, setCategories] = useState<CategoryItem[]>([]);
    const [loading, setLoading] = useState(true);

    const fallbackImages = [
        'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=800&h=600&fit=crop',
    ];

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await getCategories();
                const sortedData = [...data].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                const mappedCategories: CategoryItem[] = sortedData.map((cat, index) => ({
                    id: cat.id.toString(),
                    name: cat.name_fr,
                    nameAr: cat.name_ar,
                    slug: cat.slug,
                    image: cat.image || fallbackImages[index % fallbackImages.length],
                }));
                setCategories(mappedCategories);
            } catch (error) {
                console.error('Failed to fetch categories', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
        },
    };

    if (loading) {
        return (
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <div className="h-10 w-64 bg-gray-200 rounded mx-auto animate-pulse"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="h-80 bg-gray-200 rounded-3xl animate-pulse"></div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="py-16 bg-white">
            <div className="container mx-auto px-4">
                <motion.div
                    className={`text-center mb-16 ${isRTL ? 'rtl' : 'ltr'}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="flex justify-center mb-4">
                        <div className="w-16 h-1.5 bg-primary rounded-full"></div>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">
                        {t('home.categoriesTitle')}
                    </h2>
                </motion.div>

                <motion.div
                    className="grid grid-cols-1 md:grid-cols-3 gap-8"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    {categories.map((category) => (
                        <motion.div
                            key={category.id}
                            variants={itemVariants}
                            whileHover={{ y: -10 }}
                            className="group"
                        >
                            <Link
                                to={`/category/${category.slug}`}
                                className="block relative overflow-hidden rounded-[2.5rem] shadow-lg hover:shadow-xl transition-all duration-500 h-80 group-hover:shadow-inner"
                            >
                                {/* Image */}
                                <div className="absolute inset-0">
                                    <img
                                        src={getImageUrl(category.image)}
                                        alt={isRTL ? category.nameAr : category.name}
                                        className="w-full h-full object-cover transform group-hover:scale-110 transition-smooth-slow"
                                    />
                                </div>

                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                                {/* Content */}
                                <div className="absolute inset-0 flex items-end p-8">
                                    <div className="text-white">
                                        <h3 className="text-3xl font-bold mb-2 group-hover:text-primary-light transition-smooth">
                                            {isRTL ? category.nameAr : category.name}
                                        </h3>
                                        <div className="flex items-center gap-2 text-green-100 group-hover:gap-4 transition-smooth">
                                            <span className="font-medium">{t('common.explore')}</span>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-5 w-5"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d={isRTL ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {/* Floating Animation Indicator */}
                                <motion.div
                                    className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm rounded-full p-3"
                                    animate={{ y: [0, -8, 0] }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-6 w-6 text-white"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                    </svg>
                                </motion.div>
                            </Link>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default Categories;
